/**
 * Derives the page name the navigation service scopes action-bar nodes by.
 *
 * The vocabulary is fixed by the server: NavigationService.GetPageGroup keys its cache on the last
 * segment of `dbo.dynamicformroute.url`, lowercased. The route "/_/galisweb/item" is the page
 * "item". Do not invent names here — anything this function returns that is not a route slug simply
 * matches nothing.
 *
 * Two shapes reach it:
 *
 *   record page   /cms/galisweb/item?id=9262
 *                 the last path segment already IS the slug.
 *
 *   list page     /cms/galisweb/list?model=...ItemModel&url=cms/galisweb/item&view=1
 *                 every registry list in the product lives at the same "list" route and names the
 *                 record page it lists in `url=`. Reading the path segment here would report "list"
 *                 for all of them, so `url=` is checked FIRST.
 *
 * Returns null when there is no page to name. Null is not "match everything": PageNameMatcher fails
 * an include list closed on a null page, so a node scoped to one form stays hidden rather than
 * leaking onto a page we could not identify.
 */
export function derivePageName(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  const slug = (value: string): string | null => {
    const parts = value.split('/').filter(p => p.length > 0);
    return parts.length > 0 ? parts[parts.length - 1].toLowerCase() : null;
  };

  const decode = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  // A list page names its record page explicitly.
  const urlParam = url.match(/[?&]url=([^&#]+)/i);
  if (urlParam) {
    const fromParam = slug(decode(urlParam[1]));
    if (fromParam) {
      return fromParam;
    }
  }

  // Older list links carry only the application model. "…Collections.ItemModel" -> "item".
  const modelParam = url.match(/[?&]model=([^&#]+)/i);
  if (modelParam) {
    const modelName = decode(modelParam[1]).split('.').pop() || '';
    const stripped = modelName.replace(/Model$/i, '').toLowerCase();
    if (stripped.length > 0) {
      return stripped;
    }
  }

  // Record page: the path, without query string or fragment.
  return slug(url.split('#')[0].split('?')[0]);
}
